// Books, phones and the way hands hold them.
import * as THREE from 'three';
import { RoundedBoxGeometry } from 'three/addons/geometries/RoundedBoxGeometry.js';
import { PI } from '../../lib/math.js';
import { pick } from '../../lib/random.js';
import { M } from '../../lib/factory.js';
import { toonify } from '../../core/optimise.js';
import { aim, ik } from './seating.js';

export let pagesM, phoneM, screenM, lensM, makeBook, makePhone, holdProp;

export function buildProps() {
  // a book: cover plus a slightly smaller block of pages on the reader's side (-z); w across, h along the spine
  makeBook = function makeBook(u) {
    const g = new THREE.Group();
    g.add(new THREE.Mesh(new THREE.BoxGeometry(0.12 * u, 0.09 * u, 0.012 * u), pick(coverMs)));
    const pg = new THREE.Mesh(new THREE.BoxGeometry(0.112 * u, 0.082 * u, 0.01 * u), pagesM);
    pg.position.z = -0.008 * u;
    g.add(pg);
    g.add(
      Object.assign(
        new THREE.Mesh(new THREE.BoxGeometry(0.003 * u, 0.082 * u, 0.012 * u), propM(0x6b5a45)),
        {},
      ),
    );
    return g;
  };
  makePhone = function makePhone(u) {
    const g = new THREE.Group();
    g.add(
      new THREE.Mesh(new RoundedBoxGeometry(0.034 * u, 0.068 * u, 0.007 * u, 1, 0.003 * u), phoneM),
    );
    const sc = new THREE.Mesh(new THREE.PlaneGeometry(0.03 * u, 0.06 * u), screenM);
    sc.position.z = -0.004 * u;
    sc.rotation.y = PI;
    g.add(sc);
    return g;
  };
  holdProp = function holdProp(model, b, C, across, back, dx, sides, pole) {
    for (const s of sides) {
      const sg = s === 'L' ? 1 : -1,
        edge = C.clone().addScaledVector(across, sg * dx);
      ik(
        b('UpperArm.' + s),
        b('LowerArm.' + s),
        b('Hand.' + s),
        edge
          .clone()
          .addScaledVector(across, sg * 0.012)
          .addScaledVector(back, 0.05),
        pole(sg),
      );
      aim(b('Hand.' + s), b('Middle2.' + s), edge.clone().addScaledVector(back, -0.01));
      curlFingers(model, s, 0.85);
    }
  };
  // bench sitters, the jet-ski rider, and people standing still (bus stops, paddle boards) idling in place
  // ---------- props and activities: books, phones, sunglasses ----------
  const propM = (c, o = {}) => {
    const m = M(c, Object.assign({ flatShading: false, roughness: 0.6 }, o));
    toonify(m);
    return m;
  };
  pagesM = propM(0xf4efe2);
  phoneM = propM(0x18191c, { roughness: 0.25 });
  screenM = propM(0xbfd8f0, { emissive: 0x8fb8e8, emissiveIntensity: 0.6 });
  lensM = propM(0x0d0e10, { roughness: 0.15, metalness: 0.4 });
  const coverMs = [0xc0392b, 0x2f6fd0, 0x2e9e6a, 0xf2c014, 0x8e5bb5, 0x1d2433].map((c) => propM(c));
  // Hold a prop: the wrist goes a little behind the prop's edge (toward the body), the hand is turned so the fingers
  // point at the prop, and the fingers close round it. `across` is the model's left in world space, `back` points from
  // the prop toward the body, C is the prop centre (world), dx half the prop's width.
  const curlFingers = (model, s, amt) =>
    model.traverse((o) => {
      if (!o.isBone || !o.userData.curl || !o.name.endsWith(s)) return;
      const k = /^Thumb/.test(o.name) ? amt * 0.5 : amt;
      o.quaternion.premultiply(
        new THREE.Quaternion().setFromAxisAngle(
          new THREE.Vector3().fromArray(o.userData.curl),
          o.userData.sg * k,
        ),
      );
      o.updateMatrix();
    });
}
